import { useState } from "react";
import { getFranchiseLogo } from "../../utils/franchiseDetector";
import { franchiseIdOf, useFranchiseLogoMap } from "../../lib/franchiseLogos";
import { franchiseLogoCandidates } from "./FranchiseLogo";

interface Props {
    name: string;
    /** The establishment's `franchise`: a bare id or a populated `{ _id, name }`. */
    franchise?: unknown;
    nafCode?: string;
    emojiClassName?: string;
    imgClassName?: string;
}

/**
 * What goes inside an establishment's logo box, in this order:
 * 1. its franchise's logo, resolved like the franchise's own fiche (bundled on the brand name →
 *    back-office logo → Clearbit), when it is linked to one;
 * 2. the bundled logo matched on its own name;
 * 3. the detector's emoji (activity or 🍽️).
 * A remote URL that fails to load moves on to the next candidate instead of leaving a broken image.
 */
export function EstablishmentLogo({ name, franchise, nafCode, emojiClassName = "text-2xl", imgClassName = "w-full h-full object-cover" }: Props) {
    const logos = useFranchiseLogoMap();
    const [broken, setBroken] = useState<string[]>([]);

    const id = franchiseIdOf(franchise);
    const brand = id ? logos[id] : undefined;
    // An old cached response held bare URLs; the populated `franchise` may also carry the name.
    const populatedName = typeof franchise === "object" ? (franchise as { name?: string } | null)?.name : undefined;
    const candidates = brand && typeof brand === "object"
        ? franchiseLogoCandidates(brand)
        : populatedName ? franchiseLogoCandidates({ name: populatedName, logoUrl: null, website: null }) : [];
    const src = candidates.find((c) => !broken.includes(c));
    if (src) {
        return <img key={src} src={src} alt={`${name} logo`} className={imgClassName} onError={() => setBroken((b) => [...b, src])} />;
    }

    // detectFranchise returns null for an empty name, which getFranchiseLogo doesn't guard against.
    const local = name ? getFranchiseLogo(name, nafCode) : { logo: "🍽️", isIcon: true };
    return local.isIcon
        ? <span className={emojiClassName}>{local.logo as string}</span>
        : <img src={local.logo as string} alt={`${name} logo`} className={imgClassName} />;
}
